import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });
      formBody.append('_to', 'info@it-support.club');

      const response = await fetch('https://readdy.ai/api/form/d4tvogk0r9mt3nkms2tg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', company: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-white font-['Noto_Sans_JP']">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img src="https://public.readdy.ai/ai/img_res/0c4b5fec-5a80-4abb-b422-2ff9603ecd51.png" alt="NAWABARI" className="h-10" />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>特徴</a>
            <a href="#usage" className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>利用方法</a>
            <a href="#cases" className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>導入事例</a>
            <a href="#contact" className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>お問い合わせ</a>
            <a href="https://nawabari.net/aff/LA0000001/143bbd65/" target="_blank" rel="noopener noreferrer" className="bg-teal-600 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">今すぐ申し込む</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/d1bf06639c09601bb45172c2b3401adb.jpg" alt="Hero Background" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center w-full">
          <div className="inline-block bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <span className="text-white text-sm font-medium">初期費用0円・最短即日利用可能</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            新時代の働き方を、<br />月額1000円から
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
            登記可能な一等地住所で、あなたのビジネスに信頼を
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 mb-12">
            <div className="bg-white/95 backdrop-blur-sm px-6 py-3 rounded-lg">
              <span className="text-gray-800 font-semibold text-sm">初期費用0円</span>
            </div>
            <div className="bg-white/95 backdrop-blur-sm px-6 py-3 rounded-lg">
              <span className="text-gray-800 font-semibold text-sm">最短即日利用可</span>
            </div>
            <div className="bg-white/95 backdrop-blur-sm px-6 py-3 rounded-lg">
              <span className="text-gray-800 font-semibold text-sm">法人登記対応</span>
            </div>
          </div>
          <a href="#contact" className="inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-10 py-4 rounded-full text-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
            無料で資料請求
            <i className="ri-arrow-right-up-line text-xl"></i>
          </a>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <span className="text-sm font-medium text-gray-500 uppercase tracking-wider">NAWABARI が選ばれる理由</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 leading-tight">
              圧倒的コストパフォーマンスと、<br />充実のサービス
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-building-line',
                title: '一等地の住所利用',
                description: '東京・大阪・名古屋など主要都市の一等地住所をビジネス利用可能。信頼性の高い住所で企業イメージを向上させます。',
                color: 'bg-blue-50'
              },
              {
                icon: 'ri-coin-line',
                title: '業界最安値水準',
                description: '月額1000円から利用可能な圧倒的な低価格。初期費用も0円で、スタートアップや個人事業主の方にも最適です。',
                color: 'bg-emerald-50'
              },
              {
                icon: 'ri-file-text-line',
                title: '即日利用開始',
                description: 'オンライン申込で最短30分で審査完了。必要書類を提出後、即日からバーチャルオフィスの利用を開始できます。',
                color: 'bg-purple-50'
              },
              {
                icon: 'ri-customer-service-line',
                title: '充実サポート',
                description: '専任スタッフによる丁寧なサポート体制。郵便物転送、電話代行など、ビジネスに必要なサービスを完備しています。',
                color: 'bg-orange-50'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                <div className={`w-16 h-16 ${feature.color} rounded-2xl flex items-center justify-center mb-6`}>
                  <i className={`${feature.icon} text-3xl text-gray-700`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Usage Section */}
      <section id="usage" className="py-24 bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <span className="inline-block bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-white text-sm font-medium mb-4">簡単3ステップ</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
              登録から利用開始まで、<br />最短30分
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                number: '01',
                title: 'プラン選択',
                description: 'ご希望の住所とプランを選択。料金プランは月額1000円からご用意しています。',
                time: '約5分'
              },
              {
                number: '02',
                title: '必要書類提出',
                description: '本人確認書類と事業内容をオンラインで提出。スマホからでも簡単に手続き可能です。',
                time: '約10分'
              },
              {
                number: '03',
                title: '審査・契約',
                description: '最短30分で審査完了。契約書類はすべてオンラインで完結します。',
                time: '約30分'
              },
              {
                number: '04',
                title: '利用開始',
                description: '審査完了後、即日から住所利用が可能。法人登記もすぐに申請できます。',
                time: '即日'
              }
            ].map((step, index) => (
              <div key={index} className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 relative overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer">
                <div className="absolute top-4 right-4 text-8xl font-bold text-gray-200/30">{step.number}</div>
                <div className="relative z-10">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-gray-700 leading-relaxed mb-6 text-sm">{step.description}</p>
                  <div className="inline-block bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    {step.time}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="cases" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <span className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">導入企業の声</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
              実際に利用されている、<br />お客様の声をご紹介
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: '田中 健太',
                company: 'テックスタートアップ株式会社',
                industry: 'IT・ソフトウェア開発',
                avatar: 'TK',
                review: '起業時のコストを大幅に削減できました。一等地の住所を使えることで、クライアントからの信頼も得やすくなりました。サポートも迅速で助かっています。',
                rating: 5
              },
              {
                name: '佐藤 美咲',
                company: 'クリエイティブデザイン合同会社',
                industry: 'デザイン・クリエイティブ',
                avatar: 'SM',
                review: 'フリーランスから法人化する際に利用しました。自宅住所を公開せずにビジネスができるので、プライバシーも守れて安心です。',
                rating: 5
              },
              {
                name: '鈴木 大輔',
                company: 'グローバルコンサルティング',
                industry: 'コンサルティング',
                avatar: 'SD',
                review: '複数拠点を持つ必要があり、NAWABARIを選びました。全国主要都市に展開しているので、地域ごとのビジネス展開がスムーズになりました。',
                rating: 5
              },
              {
                name: '山田 花子',
                company: 'オンラインショップ運営',
                industry: 'EC・小売',
                avatar: 'YH',
                review: '月額1000円という低価格で一等地の住所が使えるのは本当に助かります。郵便物転送サービスも便利で、ビジネスに集中できています。',
                rating: 5
              },
              {
                name: '高橋 誠',
                company: 'マーケティングエージェンシー',
                industry: 'マーケティング・広告',
                avatar: 'TM',
                review: '申込から利用開始までが本当に早くて驚きました。急ぎで法人登記が必要だったので、即日対応していただけて大変助かりました。',
                rating: 5
              },
              {
                name: '伊藤 麻衣',
                company: 'ウェルネスコーチング',
                industry: 'ヘルスケア・ウェルネス',
                avatar: 'IM',
                review: '個人事業主として活動していますが、プロフェッショナルな印象を与えられる住所のおかげで、ビジネスチャンスが広がりました。',
                rating: 5
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">{testimonial.company}</h4>
                    <p className="text-xs text-gray-500">{testimonial.industry}</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-6 text-sm">「{testimonial.review}」</p>
                <div className="flex gap-1">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-orange-500 text-lg"></i>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">お問い合わせ</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              まずはお気軽にご相談ください
            </h2>
            <p className="text-gray-600 text-lg">
              ご不明な点やご質問がございましたら、お気軽にお問い合わせください。
            </p>
          </div>
          <form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-8 md:p-12" data-readdy-form id="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">お名前 *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="山田 太郎"
                />
              </div>
              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">会社名</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="株式会社サンプル"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">メールアドレス *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="example@email.com"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">電話番号</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="090-1234-5678"
                />
              </div>
            </div>
            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">お問い合わせ内容 *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                placeholder="お問い合わせ内容をご記入ください（500文字以内）"
              />
              <p className="text-xs text-gray-500 mt-2">{formData.message.length}/500文字</p>
            </div>
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-700 text-sm font-medium">お問い合わせを受け付けました。ありがとうございます。</p>
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm font-medium">送信に失敗しました。もう一度お試しください。</p>
              </div>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-teal-600 to-teal-700 text-white px-8 py-4 rounded-lg font-semibold hover:from-teal-700 hover:to-teal-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm"
            >
              {isSubmitting ? '送信中...' : '送信する'}
            </button>
          </form>
        </div>
      </section>

      {/* Affiliate Section */}
      <section className="py-24 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-3xl p-12 md:p-16 shadow-xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">選ばれる理由</span>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  NAWABARIで<br />ビジネスを加速させる
                </h2>
                <p className="text-gray-600 leading-relaxed mb-8 text-lg">
                  NAWABARIのバーチャルオフィスは、起業家・フリーランス・スタートアップ企業に最適なソリューションです。一等地の住所を使用することで、ビジネスの信頼性を高め、コストを大幅に削減できます。
                </p>
                <ul className="space-y-4 mb-8">
                  {[
                    '業界最安値水準の月額1000円から',
                    '全国主要都市の一等地住所が利用可能',
                    '郵便物転送・電話代行サービス完備',
                    '継続的な研修プログラム'
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-teal-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700 font-medium text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="https://nawabari.net/aff/LA0000001/143bbd65/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white px-8 py-4 rounded-full font-semibold hover:from-teal-700 hover:to-teal-800 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  今すぐ申し込む
                  <i className="ri-arrow-right-line text-xl"></i>
                </a>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20business%20partnership%20handshake%20in%20modern%20bright%20office%20environment%20with%20diverse%20team%20members%20collaborating%20around%20a%20conference%20table%2C%20natural%20window%20lighting%20creating%20warm%20atmosphere%2C%20corporate%20success%20and%20growth%20concept%20with%20clean%20minimalist%20aesthetic%20and%20professional%20attire&width=800&height=900&seq=affiliate-img-001&orientation=portrait"
                  alt="ビジネス成功イメージ"
                  className="w-full h-full object-cover object-top rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Bright%20modern%20coworking%20space%20with%20floor%20to%20ceiling%20windows%20showing%20city%20views%2C%20minimalist%20white%20interior%20design%20with%20natural%20wood%20accents%20and%20green%20plants%2C%20professional%20entrepreneurs%20working%20on%20laptops%20in%20collaborative%20environment%2C%20soft%20natural%20daylight%20creating%20inspiring%20productive%20atmosphere&width=1920&height=1080&seq=cta-bg-002&orientation=landscape"
            alt="CTA Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-wider">
            START YOUR BUSINESS TODAY
          </h2>
          <p className="text-2xl md:text-3xl text-white/90 mb-12 leading-relaxed">
            今すぐ無料で資料請求・お問い合わせ
          </p>
          <a
            href="https://nawabari.net/aff/LA0000001/143bbd65/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-4 bg-black text-white px-12 py-5 rounded-full text-lg font-semibold hover:bg-gray-900 transition-all shadow-2xl hover:shadow-3xl whitespace-nowrap cursor-pointer"
          >
            <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
              <i className="ri-mail-line text-white text-xl"></i>
            </div>
            NAWABARI サービス申し込みへ
            <i className="ri-arrow-right-up-line text-2xl"></i>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-900 to-teal-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" viewBox="0 0 1200 600">
            <path d="M0,300 Q300,200 600,300 T1200,300" stroke="currentColor" fill="none" strokeWidth="1"/>
            <path d="M0,320 Q300,220 600,320 T1200,320" stroke="currentColor" fill="none" strokeWidth="1"/>
          </svg>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-4">
              <img src="https://public.readdy.ai/ai/img_res/0c4b5fec-5a80-4abb-b422-2ff9603ecd51.png" alt="NAWABARI" className="h-10 mb-6 brightness-0 invert" />
              <p className="text-white/80 mb-6 leading-relaxed text-sm">
                あなたのビジネスを、<br />次のステージへ
              </p>
              <div className="flex gap-3">
                <a href="#" className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
              </div>
            </div>
            <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
              <div>
                <h4 className="font-bold mb-4 text-sm">サービス</h4>
                <ul className="space-y-3">
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">プラン一覧</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">オプション</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">料金表</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-4 text-sm">サポート</h4>
                <ul className="space-y-3">
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">よくある質問</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">利用ガイド</a></li>
                  <li><a href="#contact" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">お問い合わせ</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-4 text-sm">会社情報</h4>
                <ul className="space-y-3">
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">運営会社</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">拠点一覧</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">採用情報</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-4 text-sm">リソース</h4>
                <ul className="space-y-3">
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">ブログ</a></li>
                  <li><a href="#cases" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">事例紹介</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">ダウンロード</a></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/60 text-sm">© 2025 NAWABARI. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-white/60 hover:text-white transition-colors text-sm cursor-pointer">プライバシーポリシー</a>
              <a href="#" className="text-white/60 hover:text-white transition-colors text-sm cursor-pointer">利用規約</a>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors text-sm cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
